// src/components/ClientSendPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyProperties, exportClientSend } from '../api';

function ClientSendPage() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('role');

  const [properties, setProperties] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const MAX_SELECTION = 10;

  // Filters
  const [filters, setFilters] = useState({
    city: '',
    sub_locality: '',
    propertyCategory: '',
    minCarpet: '',
    maxCarpet: '',
    search: '',
    activeOnly: true,
  });

  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError('');

      const params = { page, limit: 20, status: 'approved' };  // Only approved properties for client
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      const response = await getMyProperties(params);
      setProperties(response.properties || []);
      setPagination(response.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to load properties';
      setError(message);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters({
      ...filters,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchProperties();
  };

  const handleClearFilters = () => {
    setFilters({
      city: '',
      sub_locality: '',
      propertyCategory: '',
      minCarpet: '',
      maxCarpet: '',
      search: '',
      activeOnly: true,
    });
    setPage(1);
  };

  const handleToggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(x => x !== id));
    } else {
      if (selectedIds.length >= MAX_SELECTION) {
        setError(`You can select a maximum of ${MAX_SELECTION} properties per export`);
        setTimeout(() => setError(''), 3000);
        return;
      }
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    const availableIds = properties.map(p => p._id);
    const toAdd = availableIds.filter(id => !selectedIds.includes(id));
    const canAdd = MAX_SELECTION - selectedIds.length;

    if (toAdd.length <= canAdd) {
      setSelectedIds([...selectedIds, ...toAdd]);
    } else {
      setSelectedIds([...selectedIds, ...toAdd.slice(0, canAdd)]);
      setError(`Only added ${canAdd} more (max ${MAX_SELECTION})`);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) {
      setError('Please select at least one property');
      return;
    }

    try {
      setExporting(true);
      setError('');
      await exportClientSend(selectedIds);
      setSuccessMessage(`✅ Excel downloaded with ${selectedIds.length} properties`);
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to generate Excel';
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">📤 Send Properties to Client</h1>
          <p className="text-sm text-gray-500 mt-1">
            Select up to {MAX_SELECTION} properties to generate a client-ready Excel
          </p>
        </div>
        <div className="text-sm text-gray-600">
          Available: <span className="font-bold">{pagination.total}</span>
        </div>
      </div>

      {/* Success/Error messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded mb-4">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <h3 className="font-bold text-gray-700 mb-3">🔍 Filters</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">City</label>
            <select
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="">All Cities</option>
              <option value="mumbai">Mumbai</option>
              <option value="pune">Pune</option>
              <option value="delhi">Delhi</option>
              <option value="bangalore">Bangalore</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Micromarket</label>
            <input
              type="text"
              name="sub_locality"
              value={filters.sub_locality}
              onChange={handleFilterChange}
              placeholder="e.g., Andheri East"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Type</label>
            <select
              name="propertyCategory"
              value={filters.propertyCategory}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="">All Types</option>
              <option value="office">Office</option>
              <option value="warehouse">Warehouse</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Min Carpet (sqft)</label>
            <input
              type="number"
              name="minCarpet"
              value={filters.minCarpet}
              onChange={handleFilterChange}
              placeholder="e.g., 5000"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Max Carpet (sqft)</label>
            <input
              type="number"
              name="maxCarpet"
              value={filters.maxCarpet}
              onChange={handleFilterChange}
              placeholder="e.g., 15000"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Location, building name..."
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="activeOnly"
                checked={filters.activeOnly}
                onChange={handleFilterChange}
                className="w-4 h-4"
              />
              Active only
            </label>
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <button
            onClick={handleApplyFilters}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded text-sm"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Selection bar */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="flex items-center gap-4">
          <div className="text-sm">
            <span className="font-bold text-blue-700">Selected: {selectedIds.length}</span>
            <span className="text-gray-500"> / {MAX_SELECTION}</span>
          </div>
          {selectedIds.length > 0 && (
            <button
              onClick={handleClearSelection}
              className="text-xs text-blue-700 hover:text-blue-900 underline"
            >
              Clear selection
            </button>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSelectAll}
            disabled={properties.length === 0 || selectedIds.length >= MAX_SELECTION}
            className="bg-white hover:bg-gray-50 border border-blue-300 text-blue-700 px-3 py-1.5 rounded text-sm disabled:opacity-50"
          >
            Select All on Page
          </button>
          <button
            onClick={handleExport}
            disabled={selectedIds.length === 0 || exporting}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? '⏳ Generating...' : `📊 Generate Excel (${selectedIds.length})`}
          </button>
        </div>
      </div>

      {/* Property List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-gray-500 text-lg">No properties found</p>
          <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-2">
          {properties.map((property) => {
            const isSelected = selectedIds.includes(property._id);
            const isAtLimit = selectedIds.length >= MAX_SELECTION && !isSelected;

            return (
              <div
                key={property._id}
                className={`bg-white rounded-lg shadow-sm p-4 transition-all cursor-pointer border-2 ${
                  isSelected ? 'border-green-500 bg-green-50' : 'border-transparent hover:border-gray-200'
                } ${isAtLimit ? 'opacity-60' : ''}`}
                onClick={() => !isAtLimit && handleToggleSelect(property._id)}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="pt-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleSelect(property._id)}
                      disabled={isAtLimit}
                      className="w-5 h-5 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Property info */}
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-800 mb-1">
                      {property.location || 'Untitled'}
                    </h3>

                    <p className="text-sm text-gray-600 mb-1">
                      📍 {property.sub_locality || 'N/A'}, {property.city || 'N/A'}
                    </p>

                    <div className="flex flex-wrap gap-3 text-sm text-gray-700 mt-1">
                      <span>🏢 {property.property_type || 'N/A'}</span>
                      {property.rent_per_sqft && property.rent_per_sqft !== 'N/A' && (
                        <span>💰 {property.rent_per_sqft}</span>
                      )}
                      {property.price && property.price !== 'Price on Request' && (
                        <span>💵 {property.price}</span>
                      )}
                      {property.carpet_area && property.carpet_area !== 'N/A' && (
                        <span>📐 {property.carpet_area}</span>
                      )}
                      {property.floor && property.floor !== 'N/A' && (
                        <span>🏗️ {property.floor}</span>
                      )}
                    </div>
                  </div>

                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="text-green-600 font-bold text-lg">✓</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-4 py-2 bg-white border rounded disabled:opacity-50 hover:bg-gray-50"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(Math.min(pagination.pages, page + 1))}
            disabled={page >= pagination.pages}
            className="px-4 py-2 bg-white border rounded disabled:opacity-50 hover:bg-gray-50"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default ClientSendPage;